import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { numberWithCommas } from "../../utils";
import { Branch } from "../../types/user.type";
import {
  PURCHASE_ORDER_CHAIN_LABEL,
  PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL,
  PURCHASE_ORDER_STATUS_LABEL,
  PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL,
  PurchaseOrderImportPreviewData,
} from "../../types/purchase-order.type";
import PreviewRowsTable, { AutoFillNote } from "./PreviewRowsTable";
import UnreadableRowsCard from "./UnreadableRowsCard";
import { CARD_CONTENT_SX, CARD_SX } from "./constants";

const SECONDS_PER_MINUTE = 60;

interface SummaryStatProps {
  label: string;
  value: number;
}

const SummaryStat = ({ label, value }: SummaryStatProps) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{numberWithCommas(value)}</Typography>
  </Box>
);

interface PreviewStepProps {
  preview: PurchaseOrderImportPreviewData;
  branches: Branch[];
  branchChoiceByRow: Record<number, string>;
  /** จำนวนแถว "ต้องเลือกสาขา" ที่ยังไม่ได้เลือก — ไม่บล็อก บันทึกเป็น ยังไม่ระบุสาขา */
  unchosenBranchCount: number;
  /** จำนวนแถวที่จะถูกบันทึก (ไม่รวมแถวซ้ำ และแถวเกินกำหนดส่งที่ไม่ได้ติ๊กนำเข้า) */
  savedCount: number;
  includedPastRowNos: ReadonlySet<number>;
  onTogglePastRow: (rowNo: number) => void;
  onSetAllPastRows: (include: boolean) => void;
  onSetPastRows: (rowNos: number[], include: boolean) => void;
  applying: boolean;
  onChooseBranch: (rowNo: number, companyId: string) => void;
  onBulkChooseBranch: (rowNos: number[], companyId: string) => void;
  autoFillNote: AutoFillNote | null;
  onDismissAutoFillNote: () => void;
  onConfirm: () => void;
}

/**
 * ขั้นที่ 2 ของ W2 — ผลตรวจ 4 ค่า ตารางแถวพร้อมผลตรวจรายแถว และปุ่มยืนยัน
 *
 * ยังไม่มีอะไรถูกเขียนลงฐานข้อมูลในขั้นนี้ ระบบพักแผนไว้ใต้ upload_id และปุ่มยืนยัน
 * เท่านั้นที่เขียนจริง · ไม่มีอะไรบล็อกนอกจากไฟล์ที่ไม่มีแถวให้บันทึกเลย — แถวที่ยังไม่
 * เลือกสาขาถูกบันทึกเป็น ยังไม่ระบุสาขา ให้ HO ไประบุต่อที่ รายการ PO (หลาย PO จับคู่
 * อัตโนมัติไม่ได้ และ HO ไม่ควรต้องค้างไฟล์ไว้จนกว่าจะรู้ครบทุกแถว) · แถวซ้ำและแถวที่อ่าน
 * ไม่ได้ถูกข้ามไปเฉย ๆ
 */
const PreviewStep = ({
  preview,
  branches,
  branchChoiceByRow,
  unchosenBranchCount,
  savedCount,
  includedPastRowNos,
  onTogglePastRow,
  onSetAllPastRows,
  onSetPastRows,
  applying,
  onChooseBranch,
  onBulkChooseBranch,
  autoFillNote,
  onDismissAutoFillNote,
  onConfirm,
}: PreviewStepProps) => {
  const { summary } = preview;
  const hasNothingToSave = savedCount === 0;
  const expiryMinutes = Math.round(
    preview.expires_in_seconds / SECONDS_PER_MINUTE,
  );

  return (
    <Stack gap={2}>
      <Card sx={CARD_SX}>
        <CardContent sx={CARD_CONTENT_SX}>
          <Typography variant="subtitle1">{preview.file_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            เชน {PURCHASE_ORDER_CHAIN_LABEL[preview.chain]}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" gap={4} flexWrap="wrap">
            <SummaryStat
              label={PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL.MATCHED}
              value={summary.matched}
            />
            <SummaryStat
              label={PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL.BRANCH_REQUIRED}
              value={summary.branch_required}
            />
            <SummaryStat
              label={PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL.DUPLICATE}
              value={summary.duplicate}
            />
            <SummaryStat label="อ่านไม่ได้" value={summary.unreadable} />
          </Stack>
          {/* ไม่ใช่ค่าที่ห้า: แถวเหล่านี้ถูกนับอยู่ใน จับคู่ร้านได้ / ต้องเลือกสาขา / ซ้ำ แล้ว
              จึงแยกไว้บรรทัดล่างเป็นส่วนย่อย ไม่ให้อ่านเป็นยอดที่บวกกันได้ */}
          <Typography variant="body2" color="text.secondary" mt={1}>
            ในจำนวนนี้ {PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL}{" "}
            <strong>{numberWithCommas(summary.past_delivery ?? 0)}</strong> แถว
            (นับรวมอยู่ในผลตรวจข้างบนแล้ว)
          </Typography>
        </CardContent>
      </Card>

      <PreviewRowsTable
        rows={preview.rows}
        branches={branches}
        branchChoiceByRow={branchChoiceByRow}
        onChooseBranch={onChooseBranch}
        onBulkChooseBranch={onBulkChooseBranch}
        autoFillNote={autoFillNote}
        onDismissAutoFillNote={onDismissAutoFillNote}
        includedPastRowNos={includedPastRowNos}
        onTogglePastRow={onTogglePastRow}
        onSetAllPastRows={onSetAllPastRows}
        onSetPastRows={onSetPastRows}
      />

      {preview.skipped.length > 0 && (
        <UnreadableRowsCard
          rows={preview.skipped}
          uploadId={preview.upload_id}
        />
      )}

      <Card sx={CARD_SX}>
        <CardContent sx={CARD_CONTENT_SX}>
          <Stack gap={1.5}>
            {unchosenBranchCount > 0 && (
              <Alert severity="warning">
                มี <strong>{numberWithCommas(unchosenBranchCount)} แถว</strong>{" "}
                ยังไม่ระบุสาขา — บันทึกไว้ก่อนได้ แล้วไประบุสาขาต่อที่ รายการ PO
                (สถานะ ยังไม่ระบุสาขา)
              </Alert>
            )}

            {(summary.past_delivery ?? 0) > 0 && (
              <Alert severity="info">
                มี {numberWithCommas(summary.past_delivery)} แถวที่
                {PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL} (วันส่งเลยมาแล้ว) —
                ไม่นำเข้าเว้นแต่ติ๊ก นำเข้า ·{" "}
                <strong>
                  ใบที่นำเข้าจะเป็น {PURCHASE_ORDER_STATUS_LABEL.EXPIRED} ทันที
                </strong>{" "}
                (เก็บไว้เป็นประวัติ ออกบิลไม่ได้) · นำเข้าแล้ว{" "}
                {numberWithCommas(includedPastRowNos.size)} แถว
              </Alert>
            )}

            {hasNothingToSave && (
              <Alert severity="info">ไม่มีแถวที่ต้องบันทึก</Alert>
            )}

            <Alert severity="info">
              ผลตรวจนี้หมดอายุใน {expiryMinutes} นาที
              หลังจากนั้นต้องเลือกไฟล์แล้วตรวจใหม่
            </Alert>

            <Box>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={onConfirm}
                loading={applying}
                disabled={hasNothingToSave}
                sx={{ whiteSpace: "nowrap" }}
              >
                ยืนยันบันทึก {numberWithCommas(savedCount)} ใบ
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default PreviewStep;
